import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, ClauseBlock, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
import { parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
const studentbostadNotificationRules = [
    postSigningRule({
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        resolveTriggerAt: (values) => {
            if (values.lease_type !== 'Bestämd tid')
                return null;
            const leaseEnd = parseDateValue(values.lease_end_date);
            // The standard branch keeps `notice_months` hidden/unset, so read
            // the implied 3 months from `notice_type` — otherwise the default
            // contract would silently produce no uppsägning reminder.
            const notice = values.notice_type === '3 månader (standard)'
                ? 3
                : parseNumberValue(values.notice_months);
            if (!leaseEnd || notice === null || notice <= 0)
                return null;
            return subtractMonths(leaseEnd, notice);
        },
    }),
    {
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Hyresavtalet löper ut',
        phase: 'post_signing',
        resolve: (ctx) => {
            if (ctx.values.lease_type !== 'Bestämd tid')
                return [];
            const leaseEnd = parseDateValue(ctx.values.lease_end_date);
            if (!leaseEnd)
                return [];
            return [
                {
                    triggerAt: leaseEnd,
                    fireAt: subtractMonths(leaseEnd, 4),
                    leadLabel: 'custom',
                    label: '4 månader före hyresavtalet löper ut',
                },
                {
                    triggerAt: leaseEnd,
                    fireAt: subtractMonths(leaseEnd, 2),
                    leadLabel: 'custom',
                    label: '2 månader före hyresavtalet löper ut',
                },
            ];
        },
    },
];
export const metadata = {
    templateKey: 'studentbostad',
    version: 'v1',
    category: 'Studentbostad',
    title: 'Hyreskontrakt för studentbostad',
    shortTitle: 'Studentbostadsavtal',
    titleSuffix: { key: 'property_landlord_apt_no', prefix: 'lgh' },
    formNumber: 'Formulär R-14',
    preamble: 'Lägenheten hyrs ut för att användas till studentbostad. Mellan hyresvärd och hyresgäst har följande avtal träffats.',
    description: 'Bostadshyresavtal för studentbostad — lägenheten är avsedd för person som bedriver aktiva universitets-, högskole- eller därmed jämförliga studier.\n\n' +
        'Avtalet stödjer både enkelrum i korridorboende (med gemensamma utrymmen) och hela lägenheter. Med hänsyn till normalstudietid är hyresförhållandet avsett att upphöra efter maximalt cirka fem år, men kan förlängas på hyresgästens skriftliga begäran om aktiva studier fortgår eller om synnerliga skäl föreligger.\n\n' +
        'Vem undertecknar: hyresvärden och hyresgästen via Scrive.\n\n' +
        'För vanliga bostadshyresavtal — använd istället mallen Bostadshyresavtal.',
    status: 'active',
    signingMode: 'scrive',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            signingLabel: 'Hyresvärden',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee',
            label: 'Hyresgäst',
            signingLabel: 'Hyresgästen',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
    ],
    // Party projection (ADR-0065) — lessor is an org, lessee a private person.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            idHint: 'org',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address' },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'lessee',
            section: 'hyresgast',
            idHint: 'person',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                {
                    attr: 'id_number',
                    key: 'tenant_personnummer',
                    requiredAtFinal: true,
                    help: 'PII — ej sökbart',
                },
                { attr: 'address', key: 'tenant_address' },
                { attr: 'phone', key: 'tenant_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility, so the conditional Förhandlingsklausul no
    // longer leaves a gap when hidden. `anvandning` and `underskrift` are
    // field-less sections (static text / signing block) — declared so they
    // get a number, but carry no editor field.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'lagenhets-adress', title: 'Lägenhetens adress' },
        { ref: 'anvandning', title: 'Lägenhetens användning' },
        { ref: 'storlek', title: 'Lägenhetstyp och storlek' },
        { ref: 'tillbehor', title: 'Till lägenheten hör' },
        { ref: 'hyrestid', title: 'Hyrestid' },
        { ref: 'uppsagning', title: 'Uppsägning' },
        { ref: 'hyressattningsmodell', title: 'Hyressättningsmodell' },
        {
            ref: 'forhandlingsklausul',
            title: 'Förhandlingsklausul',
            visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
        },
        { ref: 'hyra', title: 'Hyra' },
        { ref: 'tillagg', title: 'Tillägg' },
        { ref: 'korridorstadning', title: 'Korridor- och trappstädning' },
        { ref: 'betalning', title: 'Hyrans betalning' },
        { ref: 'deposition', title: 'Deposition' },
        { ref: 'garage', title: 'Garage / Bilplats' },
        { ref: 'reservnycklar', title: 'Reservnycklar' },
        { ref: 'sarskilda-bestammelser', title: 'Särskilda bestämmelser' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    inputSections: [
        {
            id: 'objektet',
            label: 'Hyresobjektet',
            sectionRefs: ['lagenhets-adress', 'anvandning', 'tillbehor'],
        },
        {
            id: 'lagenhetstyp',
            label: 'Lägenhetstyp och storlek',
            sectionRefs: ['storlek'],
        },
        {
            id: 'hyrestid-uppsagning',
            label: 'Hyrestid & uppsägning',
            sectionRefs: ['hyrestid', 'uppsagning'],
        },
        {
            id: 'hyra-avgifter',
            label: 'Hyra & avgifter',
            sectionRefs: [
                'hyressattningsmodell',
                'forhandlingsklausul',
                'hyra',
                'tillagg',
                'korridorstadning',
                'betalning',
                'deposition',
            ],
        },
        {
            id: 'ovrigt',
            label: 'Övrigt',
            sectionRefs: ['garage', 'reservnycklar', 'sarskilda-bestammelser'],
        },
    ],
    notificationRules: studentbostadNotificationRules,
};
export const variableSchema = [
    // Avtalsuppgifter
    {
        key: 'contract_number',
        label: 'Avtalsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        requiredAtFinal: true,
        help: 'Egen referens på avtalet, t.ex. S-2042.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Hyresvärd / Hyresgäst — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // Lägenhetens adress
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_staircase',
        label: 'Trappor',
        type: 'text',
        section: 'lagenhets-adress',
    },
    {
        key: 'property_landlord_apt_no',
        label: 'Hyresvärdens lägenhetsnummer',
        type: 'text',
        section: 'lagenhets-adress',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'property_address',
        label: 'Postadress',
        type: 'textarea',
        section: 'lagenhets-adress',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'property_official_apt_no',
        label: 'Officiellt lägenhetsnummer',
        type: 'text',
        section: 'lagenhets-adress',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Lägenhetens storlek
    {
        key: 'is_corridor_room',
        label: 'Rum i korridor',
        type: 'boolean',
        section: 'storlek',
        default: true,
        help: 'avser ett rum i korridor',
    },
    // Rum i korridor — sub-options (ST-4). Bath/wc are multi_select so the
    // tenant can hold both a private and a shared facility; the kitchen is a
    // mutually-exclusive choice.
    {
        key: 'corridor_bath',
        label: 'Badrum',
        type: 'multi_select',
        section: 'storlek',
        visibleWhen: { is_corridor_room: true },
        options: [
            { value: 'Privat badrum', label: 'Privat badrum', code: 'privat', summary: 'Tenant has a private bathroom.' },
            { value: 'Gemensamt badrum', label: 'Gemensamt badrum', code: 'gemensamt', summary: 'Tenant shares a bathroom with other rooms.' },
        ],
        help: 'Kan välja både privat och gemensamt.',
    },
    {
        key: 'corridor_bath_shared_count',
        label: 'Gemensamt badrum delas med (antal rum)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'storlek',
        visibleWhen: { is_corridor_room: true, corridor_bath: 'Gemensamt badrum' },
    },
    {
        key: 'corridor_wc',
        label: 'WC',
        type: 'multi_select',
        section: 'storlek',
        visibleWhen: { is_corridor_room: true },
        options: [
            { value: 'Privat wc', label: 'Privat wc', code: 'privat', summary: 'Tenant has a private toilet.' },
            { value: 'Gemensam wc', label: 'Gemensam wc', code: 'gemensam', summary: 'Tenant shares a toilet with other rooms.' },
        ],
        help: 'Kan välja både privat och gemensam.',
    },
    {
        key: 'corridor_wc_shared_count',
        label: 'Gemensam wc delas med (antal rum)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'storlek',
        visibleWhen: { is_corridor_room: true, corridor_wc: 'Gemensam wc' },
    },
    {
        key: 'corridor_kitchen',
        label: 'Kök',
        type: 'select',
        section: 'storlek',
        visibleWhen: { is_corridor_room: true },
        requiredAtFinal: true,
        default: 'Endast gemensamt kök',
        options: [
            {
                value: 'Pentry i rum med gemensamt kök',
                label: 'Pentry i rum med gemensamt kök',
                code: 'pentry_och_gemensamt',
                summary: 'Kitchenette in the room plus access to a shared kitchen.',
            },
            { value: 'Endast gemensamt kök', label: 'Endast gemensamt kök', code: 'endast_gemensamt', summary: 'Only a shared kitchen, no in-room kitchenette.' },
        ],
    },
    {
        key: 'corridor_room_count',
        label: 'Antal rum i korridor',
        type: 'number',
        min: 0,
        integer: true,
        section: 'storlek',
        visibleWhen: { is_corridor_room: true },
        requiredAtFinal: true,
    },
    // Ej rum i korridor: vanliga "antal rum och kök"-alternativen från
    // bostadshyra (ST-5).
    {
        key: 'apartment_type',
        label: 'Antal rum och kök',
        type: 'select',
        section: 'storlek',
        visibleWhen: { is_corridor_room: false },
        requiredAtFinal: true,
        options: [
            { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: '1_rok_kokskap', summary: 'One room with a kitchen cabinet.' },
            { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: '1_rok_kokvra', summary: 'One room with a kitchenette nook.' },
            { value: '1 rum och kök', label: '1 rum och kök', code: '1_rok', summary: 'One room and a kitchen.' },
            { value: '1,5 rum och kök', label: '1,5 rum och kök', code: '1_5_rok', summary: 'One and a half rooms and a kitchen.' },
            { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: '2_rok_kokvra', summary: 'Two rooms with a kitchenette nook.' },
            { value: '2 rum och kök', label: '2 rum och kök', code: '2_rok', summary: 'Two rooms and a kitchen.' },
            { value: '2,5 rum och kök', label: '2,5 rum och kök', code: '2_5_rok', summary: 'Two and a half rooms and a kitchen.' },
            { value: '3 rum och kök', label: '3 rum och kök', code: '3_rok', summary: 'Three rooms and a kitchen.' },
            { value: '3,5 rum och kök', label: '3,5 rum och kök', code: '3_5_rok', summary: 'Three and a half rooms and a kitchen.' },
            { value: '4 rum och kök', label: '4 rum och kök', code: '4_rok', summary: 'Four rooms and a kitchen.' },
            { value: '4,5 rum och kök', label: '4,5 rum och kök', code: '4_5_rok', summary: 'Four and a half rooms and a kitchen.' },
            { value: '5 rum och kök', label: '5 rum och kök', code: '5_rok', summary: 'Five rooms and a kitchen.' },
            { value: '6 rum och kök', label: '6 rum och kök', code: '6_rok', summary: 'Six rooms and a kitchen.' },
            { value: '7 rum och kök', label: '7 rum och kök', code: '7_rok', summary: 'Seven rooms and a kitchen.' },
            { value: '8 rum och kök', label: '8 rum och kök', code: '8_rok', summary: 'Eight rooms and a kitchen.' },
        ],
    },
    {
        key: 'apartment_area',
        label: 'Area (m²)',
        type: 'number',
        min: 0,
        section: 'storlek',
        requiredAtFinal: true,
    },
    // §5 punkt 1 (flyttad från särskilda bestämmelser, ST-6).
    {
        key: 'shared_facilities',
        label: 'Gemensamma utrymmen i korridoren',
        type: 'textarea',
        section: 'storlek',
        visibleWhen: { is_corridor_room: true },
        help: 'Beskriv vilka utrymmen hyresgästen tillsammans med övriga studenter har gemensam tillgång till (t.ex. kök, badrum, gemensamhetsutrymme).',
    },
    // Till lägenheten hör
    {
        key: 'has_attic',
        label: 'Vindsutrymme ingår',
        type: 'boolean',
        section: 'tillbehor',
    },
    {
        key: 'attic_number',
        label: 'Vindsutrymme nr',
        type: 'text',
        section: 'tillbehor',
        visibleWhen: { has_attic: true },
    },
    {
        key: 'has_basement',
        label: 'Källarutrymme ingår',
        type: 'boolean',
        section: 'tillbehor',
    },
    {
        key: 'basement_number',
        label: 'Källarutrymme nr',
        type: 'text',
        section: 'tillbehor',
        visibleWhen: { has_basement: true },
    },
    {
        key: 'has_other_storage',
        label: 'Annat utrymme ingår',
        type: 'boolean',
        section: 'tillbehor',
    },
    {
        key: 'other_storage_number',
        label: 'Annat utrymme nr',
        type: 'text',
        section: 'tillbehor',
        visibleWhen: { has_other_storage: true },
    },
    // Hyrestid
    {
        key: 'lease_type',
        label: 'Hyresform',
        type: 'select',
        section: 'hyrestid',
        requiredAtFinal: true,
        default: 'Bestämd tid',
        options: [
            { value: 'Tillsvidare', label: 'Tillsvidare', code: 'tillsvidare', summary: 'Open-ended lease (until further notice).' },
            { value: 'Bestämd tid', label: 'Bestämd tid', code: 'bestamd_tid', summary: 'Fixed-term lease with an end date.' },
        ],
    },
    {
        key: 'lease_start_date',
        label: 'Startdatum',
        type: 'date',
        section: 'hyrestid',
        requiredAtFinal: true,
    },
    {
        key: 'lease_end_date',
        label: 'Slutdatum',
        type: 'date',
        section: 'hyrestid',
        durationFrom: 'lease_start_date',
        visibleWhen: { lease_type: 'Bestämd tid' },
    },
    // Uppsägning
    {
        key: 'notice_type',
        label: 'Uppsägningstyp',
        type: 'select',
        section: 'uppsagning',
        requiredAtFinal: true,
        default: '3 månader (standard)',
        options: [
            { value: '3 månader (standard)', label: '3 månader (standard)', code: 'tre_manader', summary: 'Standard three-month notice period.' },
            { value: 'Annat antal månader', label: 'Annat antal månader', code: 'annat_antal', summary: 'A custom number of months for the notice period.' },
        ],
    },
    {
        key: 'notice_months',
        label: 'Antal månader',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        visibleWhen: { notice_type: 'Annat antal månader' },
    },
    {
        key: 'extension_months',
        label: 'Förlängning (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        default: 3,
        visibleWhen: { lease_type: 'Bestämd tid' },
        help: 'Hur lång period kontraktet förlängs med i taget om uppsägning inte sker.',
    },
    // Hyressättningsmodell
    {
        key: 'rent_model',
        label: 'Hyressättningsmodell',
        type: 'select',
        section: 'hyressattningsmodell',
        requiredAtFinal: true,
        default: 'Kollektiva hyresförhandlingar',
        options: [
            { value: 'Kollektiva hyresförhandlingar', label: 'Kollektiva hyresförhandlingar', code: 'kollektiva', summary: 'Rent set through collective negotiations.' },
            { value: 'Tidsbestämt hyressättningsavtal', label: 'Tidsbestämt hyressättningsavtal', code: 'tidsbestamt_avtal', summary: 'Rent set by a time-limited rent-setting agreement.' },
            {
                value: 'Direkta förhandlingar mellan parterna',
                label: 'Direkta förhandlingar mellan parterna',
                code: 'direkta',
                summary: 'Rent set by direct negotiation between the parties.',
            },
        ],
    },
    {
        key: 'rent_model_annex',
        label: 'Bilaga (hyressättningsavtal)',
        type: 'text',
        section: 'hyressattningsmodell',
        visibleWhen: { rent_model: 'Tidsbestämt hyressättningsavtal' },
        help: 'Hänvisning till bifogat hyressättningsavtal.',
    },
    // Förhandlingsklausul
    {
        key: 'negotiation_clause',
        label: 'Förhandlingsklausul gäller',
        type: 'boolean',
        section: 'forhandlingsklausul',
        default: true,
        visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
        help: 'Endast aktuellt vid kollektiva hyresförhandlingar.',
    },
    {
        key: 'tenant_org',
        label: 'Hyresgästorganisation',
        type: 'select_with_freetext',
        section: 'forhandlingsklausul',
        visibleWhen: { rent_model: 'Kollektiva hyresförhandlingar' },
        options: [
            { value: 'Djurgårdens hyresgästförening (802531-4702)', label: 'Djurgårdens hyresgästförening (802531-4702)', code: 'djurgarden', summary: 'Djurgårdens hyresgästförening, org.nr 802531-4702.' },
            { value: 'Föreningen för Sveriges Hyresgäster (802550-7990)', label: 'Föreningen för Sveriges Hyresgäster (802550-7990)', code: 'fsh', summary: 'Föreningen för Sveriges Hyresgäster, org.nr 802550-7990.' },
            { value: 'Göteborgs boendeförening (857205-6219)', label: 'Göteborgs boendeförening (857205-6219)', code: 'goteborg_boende', summary: 'Göteborgs boendeförening, org.nr 857205-6219.' },
            { value: 'Hyresgästföreningen (802001-5106)', label: 'Hyresgästföreningen (802001-5106)', code: 'hgf', summary: 'Hyresgästföreningen (the national tenants’ union), org.nr 802001-5106.' },
            { value: 'Hyresgästföreningen Uppsala Studentbostäder (817602-1486)', label: 'Hyresgästföreningen Uppsala Studentbostäder (817602-1486)', code: 'hgf_uppsala_student', summary: 'Hyresgästföreningen Uppsala Studentbostäder, org.nr 817602-1486.' },
            { value: 'Hyresrätt Sverige (802510-4434)', label: 'Hyresrätt Sverige (802510-4434)', code: 'hyresratt_sverige', summary: 'Hyresrätt Sverige, org.nr 802510-4434.' },
            { value: 'Råckstahyresgästernas intresseförening (802014-2876)', label: 'Råckstahyresgästernas intresseförening (802014-2876)', code: 'racksta', summary: 'Råckstahyresgästernas intresseförening, org.nr 802014-2876.' },
            { value: 'Stockholms Studenthyresgästers Förening (802009-1354)', label: 'Stockholms Studenthyresgästers Förening (802009-1354)', code: 'sshf', summary: 'Stockholms Studenthyresgästers Förening, org.nr 802009-1354.' },
        ],
    },
    // Hyra
    {
        key: 'rent_amount',
        label: 'Hyra (kr/mån)',
        type: 'currency',
        min: 0,
        section: 'hyra',
        requiredAtFinal: true,
    },
    // ST-7 — hyra utgår endast under del av året (hyresfria månader).
    {
        key: 'rent_partial_year',
        label: 'Hyra utgår endast under del av året',
        type: 'boolean',
        section: 'hyra',
        default: false,
        help: 'Kryssa i om hyra endast utgår vissa månader per år.',
    },
    {
        key: 'rent_months_per_year',
        label: 'Antal månader per år hyra utgår',
        type: 'number',
        min: 0,
        max: 12,
        integer: true,
        section: 'hyra',
        visibleWhen: { rent_partial_year: true },
    },
    {
        key: 'rent_free_months_description',
        label: 'Hyresfria månader',
        type: 'textarea',
        section: 'hyra',
        visibleWhen: { rent_partial_year: true },
        help: 'Ange vilka månader som är hyresfria (t.ex. juni–augusti).',
    },
    {
        key: 'rent_includes_member_fee',
        label: 'Avgift till hyresgästorganisation ingår',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'NEJ',
        options: [
            { value: 'JA', label: 'JA', code: 'ja', summary: 'The tenant-organisation fee is included in the rent.' },
            { value: 'NEJ', label: 'NEJ', code: 'nej', summary: 'The tenant-organisation fee is not included in the rent.' },
        ],
    },
    {
        key: 'rent_includes_membership',
        label: 'Medlemskap i förhandlande hyresgästorganisation ingår',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'NEJ',
        options: [
            { value: 'JA', label: 'JA', code: 'ja', summary: 'Membership in the negotiating tenant organisation is included.' },
            { value: 'NEJ', label: 'NEJ', code: 'nej', summary: 'Membership in the negotiating tenant organisation is not included.' },
        ],
    },
    {
        key: 'rent_type',
        label: 'Hyrestyp',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'Total hyra',
        options: [
            { value: 'Total hyra', label: 'Total hyra', code: 'total', summary: 'Rent is all-inclusive (total rent).' },
            {
                value: 'Hyra exkl. tillägg',
                label: 'Hyra exkl. nedan markerade tillägg',
                code: 'exkl_tillagg',
                summary: 'Rent excludes the add-ons marked below, billed separately.',
            },
        ],
    },
    // Tillägg
    {
        key: 'heating_included',
        label: 'Uppvärmning',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        default: 'Ingår i hyran',
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'individuell_matning', summary: 'Heating is individually metered and billed separately.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Heating is included in the rent.' },
        ],
    },
    {
        key: 'cold_water_included',
        label: 'Kallvatten',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        default: 'Ingår i hyran',
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'individuell_matning', summary: 'Cold water is individually metered and billed separately.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Cold water is included in the rent.' },
        ],
    },
    {
        key: 'hot_water_included',
        label: 'Varmvatten',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        default: 'Ingår i hyran',
        options: [
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'individuell_matning', summary: 'Hot water is individually metered and billed separately.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Hot water is included in the rent.' },
        ],
    },
    {
        key: 'electricity_included',
        label: 'Hushållsel',
        type: 'select',
        section: 'tillagg',
        requiredAtFinal: true,
        default: 'Eget abonnemang',
        options: [
            { value: 'Eget abonnemang', label: 'Eget abonnemang', code: 'eget_abonnemang', summary: 'Tenant holds their own electricity subscription.' },
            { value: 'Individuell mätning och debitering', label: 'Individuell mätning och debitering', code: 'individuell_matning', summary: 'Electricity is individually metered and billed separately.' },
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Electricity is included in the rent.' },
        ],
    },
    {
        key: 'extras_included',
        label: 'Tillval av utrustning',
        type: 'boolean',
        section: 'tillagg',
        default: false,
    },
    {
        key: 'extras_description',
        label: 'Beskrivning av tillval',
        type: 'textarea',
        section: 'tillagg',
        visibleWhen: { extras_included: true },
    },
    // Korridor- och trappstädning
    {
        key: 'corridor_cleaning',
        label: 'Korridor- och trappstädning',
        type: 'select',
        section: 'korridorstadning',
        requiredAtFinal: true,
        default: 'Ingår i hyran',
        options: [
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'ingar', summary: 'Corridor and stair cleaning is included in the rent.' },
            {
                value: 'Ombesörjs och bekostas av hyresgästen',
                label: 'Ombesörjs och bekostas av hyresgästen',
                code: 'hyresgast',
                summary: 'Tenant arranges and pays for corridor and stair cleaning.',
            },
        ],
    },
    // Hyrans betalning
    {
        key: 'payment_type',
        label: 'Betalningssätt',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'Enligt avisering i faktura',
        options: [
            {
                value: 'Enligt avisering i faktura',
                label: 'Enligt avisering i faktura (rekommenderas)',
                code: 'avisering',
                summary: 'Paid per invoice notice (recommended).',
            },
            { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'Kontonummer',
        type: 'text',
        section: 'betalning',
        visibleWhen: { payment_type: ['Plusgiro', 'Bankgiro'] },
    },
    // Deposition
    {
        key: 'deposit_required',
        label: 'Deposition krävs',
        type: 'select',
        section: 'deposition',
        requiredAtFinal: true,
        default: 'NEJ',
        options: [
            { value: 'JA', label: 'JA', code: 'ja', summary: 'A deposit is required.' },
            { value: 'NEJ', label: 'NEJ', code: 'nej', summary: 'No deposit is required.' },
        ],
    },
    {
        key: 'deposit_months',
        label: 'Depositionsbelopp (månadshyror)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'deposition',
        visibleWhen: { deposit_required: 'JA' },
    },
    {
        key: 'deposit_return_days',
        label: 'Återbetalning inom (dagar)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'deposition',
        visibleWhen: { deposit_required: 'JA' },
        default: 30,
    },
    // Garage / Bilplats
    {
        key: 'parking_arrangement',
        label: 'Garage / Bilplats',
        type: 'select',
        section: 'garage',
        requiredAtFinal: true,
        default: 'Inget garage / ingen bilplats ingår',
        options: [
            {
                value: 'Inget garage / ingen bilplats ingår',
                label: 'Inget garage / ingen bilplats ingår',
                code: 'inget',
                summary: 'No garage or parking space is included.',
            },
            { value: 'Särskilt avtal gäller', label: 'Särskilt avtal gäller', code: 'sarskilt_avtal', summary: 'A separate agreement governs the garage or parking space.' },
            {
                value: 'Avgift för garage / bilplats ingår i hyran',
                label: 'Avgift för garage / bilplats ingår i hyran',
                code: 'ingar_i_hyran',
                summary: 'The garage or parking fee is included in the rent.',
            },
        ],
    },
    {
        key: 'parking_spot_number',
        label: 'Garage- / bilplatsnummer',
        type: 'text',
        section: 'garage',
        visibleWhen: {
            parking_arrangement: 'Avgift för garage / bilplats ingår i hyran',
        },
    },
    // Reservnycklar
    {
        key: 'spare_key_consent',
        label: 'Reservnyckel — hyresgästens medgivande',
        type: 'select',
        section: 'reservnycklar',
        requiredAtFinal: true,
        default: 'Medger',
        options: [
            { value: 'Medger', label: 'Medger', code: 'medger', summary: 'Tenant consents to the landlord holding a spare key.' },
            { value: 'Medger ej', label: 'Medger ej', code: 'medger_ej', summary: 'Tenant does not consent to the landlord holding a spare key.' },
        ],
    },
    // Särskilda bestämmelser
    {
        key: 'inventory_included',
        label: 'Lägenheten hyrs ut med inventarier och annan utrustning',
        type: 'boolean',
        section: 'sarskilda-bestammelser',
        default: false,
    },
    {
        key: 'inventory_description',
        label: 'Beskrivning av inventarier',
        type: 'textarea',
        section: 'sarskilda-bestammelser',
        visibleWhen: { inventory_included: true },
        help: 'Beskriv kortfattat de inventarier som ingår, eller hänvisa till bilaga.',
    },
];
export const sampleValues = {
    contract_number: 'S-2042',
    landlord_name: 'Stiftelsen Stockholms Studentbostäder',
    landlord_org_number: '802002-1234',
    landlord_address: 'Korridorgatan 5\n113 60 Stockholm',
    landlord_phone: '08-555 80 00',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '20040412-1234',
    tenant_address: 'Föräldragatan 12\n753 18 Uppsala',
    tenant_phone: '070-123 45 67',
    property_municipality: 'Stockholm',
    property_designation: 'Studenten 7',
    property_staircase: '3 tr',
    property_landlord_apt_no: 'K-308',
    property_address: 'Studentvägen 5\n114 18 Stockholm',
    property_official_apt_no: '0180-0308',
    is_corridor_room: true,
    corridor_bath: ['Privat badrum'],
    corridor_wc: ['Gemensam wc'],
    corridor_wc_shared_count: 4,
    corridor_kitchen: 'Pentry i rum med gemensamt kök',
    corridor_room_count: 1,
    apartment_area: 14,
    has_attic: false,
    has_basement: true,
    basement_number: 'K-308',
    has_other_storage: false,
    lease_type: 'Bestämd tid',
    lease_start_date: '2026-08-15',
    lease_end_date: '2027-06-30',
    notice_type: '3 månader (standard)',
    extension_months: 3,
    rent_model: 'Kollektiva hyresförhandlingar',
    negotiation_clause: true,
    tenant_org: 'Hyresgästföreningen (802001-5106)',
    rent_amount: 4500,
    rent_partial_year: true,
    rent_months_per_year: 9,
    rent_free_months_description: 'Hyra utgår inte under juni, juli och augusti.',
    rent_includes_member_fee: 'NEJ',
    rent_includes_membership: 'NEJ',
    rent_type: 'Total hyra',
    heating_included: 'Ingår i hyran',
    cold_water_included: 'Ingår i hyran',
    hot_water_included: 'Ingår i hyran',
    electricity_included: 'Ingår i hyran',
    extras_included: false,
    corridor_cleaning: 'Ombesörjs och bekostas av hyresgästen',
    payment_type: 'Enligt avisering i faktura',
    deposit_required: 'JA',
    deposit_months: 1,
    deposit_return_days: 30,
    parking_arrangement: 'Inget garage / ingen bilplats ingår',
    spare_key_consent: 'Medger',
    shared_facilities: 'Kök med matplats, två badrum, gemensamt vardagsrum samt tvättstuga.',
    inventory_included: true,
    inventory_description: 'Säng, skrivbord, stol, garderob och bokhylla. Se separat inventarielista (bilaga 1).',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
/** True when a multi_select value array contains `option`. */
const has = (arr, option) => Array.isArray(arr) && arr.includes(option);
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("div", { className: "mb-2 flex flex-wrap gap-x-8 gap-y-1 text-[10px] text-stone-700", children: _jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Avtalsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) })] }) }), _jsx("p", { className: "mb-4 text-[10px] italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsx(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessee", values: values }) })] }), _jsxs(Section, { sectionRef: "lagenhets-adress", title: "L\u00E4genhetens adress", children: [_jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Trappor", children: v(values.property_staircase) }), _jsx(FieldRow, { label: "Hyresv\u00E4rdens l\u00E4genhetsnummer", children: v(values.property_landlord_apt_no) }), _jsx(FieldRow, { label: "Postadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Officiellt l\u00E4genhetsnummer", children: v(values.property_official_apt_no) })] }), _jsx(Section, { sectionRef: "anvandning", title: "L\u00E4genhetens anv\u00E4ndning", children: _jsx("p", { className: "text-[10.5px] leading-relaxed", children: "L\u00E4genheten hyrs ut f\u00F6r att anv\u00E4ndas till studentbostad." }) }), _jsxs(Section, { sectionRef: "storlek", title: "L\u00E4genhetstyp och storlek", children: [_jsx(FieldRow, { label: "Rum i korridor", children: values.is_corridor_room === true ? '☑' : '☐' }), cond(mode, values.is_corridor_room === true) && (_jsxs("div", { className: "mt-1", children: [_jsx(FieldRow, { label: "Antal rum i korridor", children: num(values.corridor_room_count) }), _jsx(FieldRow, { label: "Badrum", children: values.is_corridor_room === true ? (_jsxs("span", { className: "space-x-3", children: [_jsxs("span", { children: [has(values.corridor_bath, 'Privat badrum') ? '☑' : '☐', ' ', "Privat badrum"] }), _jsxs("span", { children: [has(values.corridor_bath, 'Gemensamt badrum') ? '☑' : '☐', ' ', "Gemensamt badrum", cond(mode, has(values.corridor_bath, 'Gemensamt badrum')) && (_jsxs(_Fragment, { children: [' ', "(delas med:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.corridor_bath_shared_count) }), ' ', "rum)"] }))] })] })) : ('') }), _jsx(FieldRow, { label: "WC", children: values.is_corridor_room === true ? (_jsxs("span", { className: "space-x-3", children: [_jsxs("span", { children: [has(values.corridor_wc, 'Privat wc') ? '☑' : '☐', " Privat wc"] }), _jsxs("span", { children: [has(values.corridor_wc, 'Gemensam wc') ? '☑' : '☐', ' ', "Gemensam wc", cond(mode, has(values.corridor_wc, 'Gemensam wc')) && (_jsxs(_Fragment, { children: [' ', "(delas med:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.corridor_wc_shared_count) }), ' ', "rum)"] }))] })] })) : ('') }), _jsx(FieldRow, { label: "K\u00F6k", children: v(values.corridor_kitchen) })] })), cond(mode, values.is_corridor_room === false) && (_jsx(FieldRow, { label: "Antal rum och k\u00F6k", children: v(values.apartment_type) })), _jsx(FieldRow, { label: "Area (m\u00B2)", children: num(values.apartment_area) }), cond(mode, values.is_corridor_room === true) && (_jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: ["Ut\u00F6ver ovan angivet rum i korridor m.m. har hyresg\u00E4sten tillsammans med \u00F6vriga studenter i korridoren gemensam tillg\u00E5ng till", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_facilities) }), "."] }))] }), _jsx(Section, { sectionRef: "tillbehor", title: "Till l\u00E4genheten h\u00F6r", children: _jsxs("div", { className: "grid grid-cols-3 gap-6", children: [_jsxs("div", { children: [_jsx(FieldRow, { label: "Vindsutrymme", children: values.has_attic === true ? '☑' : '☐' }), cond(mode, values.has_attic === true) && (_jsx(FieldRow, { label: "Vindsutrymme nr", children: v(values.attic_number) }))] }), _jsxs("div", { children: [_jsx(FieldRow, { label: "K\u00E4llarutrymme", children: values.has_basement === true ? '☑' : '☐' }), cond(mode, values.has_basement === true) && (_jsx(FieldRow, { label: "K\u00E4llarutrymme nr", children: v(values.basement_number) }))] }), _jsxs("div", { children: [_jsx(FieldRow, { label: "Annat utrymme", children: values.has_other_storage === true ? '☑' : '☐' }), cond(mode, values.has_other_storage === true) && (_jsx(FieldRow, { label: "Annat utrymme nr", children: v(values.other_storage_number) }))] })] }) }), _jsxs(Section, { sectionRef: "hyrestid", title: "Hyrestid", children: [_jsx(FieldRow, { label: "Hyresform", children: v(values.lease_type) }), _jsx(FieldRow, { label: "Startdatum", children: v(values.lease_start_date) }), cond(mode, values.lease_type === 'Bestämd tid') && (_jsx(FieldRow, { label: "Slutdatum", children: v(values.lease_end_date) }))] }), _jsxs(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning", children: [_jsx(FieldRow, { label: "Upps\u00E4gningstyp", children: v(values.notice_type) }), cond(mode, values.notice_type === 'Annat antal månader') && (_jsx(FieldRow, { label: "Antal m\u00E5nader", children: num(values.notice_months) })), cond(mode, values.lease_type === 'Bestämd tid') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "F\u00F6rl\u00E4ngning (m\u00E5nader)", children: num(values.extension_months) }), _jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: ["Upps\u00E4gning ska ske skriftligen tidigast till m\u00E5nadsskifte som intr\u00E4ffar n\u00E4rmast efter tre m\u00E5nader fr\u00E5n upps\u00E4gningen. I annat fall \u00E4r kontraktet f\u00F6rl\u00E4ngt med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.extension_months) }), ' ', "m\u00E5nad(er) f\u00F6r varje g\u00E5ng."] })] }))] }), _jsxs(Section, { sectionRef: "hyressattningsmodell", title: "Hyress\u00E4ttningsmodell", children: [_jsx(FieldRow, { label: "Modell", children: v(values.rent_model) }), cond(mode, values.rent_model === 'Tidsbestämt hyressättningsavtal') && (_jsx(FieldRow, { label: "Bilaga (hyress\u00E4ttningsavtal)", children: v(values.rent_model_annex) }))] }), _jsxs(Section, { sectionRef: "forhandlingsklausul", title: "F\u00F6rhandlingsklausul", children: [_jsx(FieldRow, { label: "F\u00F6rhandlingsklausul g\u00E4ller", children: values.negotiation_clause === true ? '☑' : '☐' }), _jsx(FieldRow, { label: "Hyresg\u00E4storganisation", children: v(values.tenant_org) }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: "F\u00F6rhandlingsklausul g\u00E4ller f\u00F6r hyresavtalet, vilket inneb\u00E4r att hyran f\u00F6r l\u00E4genheten f\u00F6rhandlas fram med den hyresg\u00E4storganisation som \u00E4r part i den f\u00F6r huset g\u00E4llande f\u00F6rhandlingsordningen." })] }), _jsxs(Section, { sectionRef: "hyra", title: "Hyra", children: [_jsx(FieldRow, { label: "Hyra (kr/m\u00E5n)", children: num(values.rent_amount) }), cond(mode, values.rent_partial_year === true) && (_jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: ["Hyra utg\u00E5r", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.rent_months_per_year) }), ' ', "m\u00E5nader per \u00E5r. Hyresfria m\u00E5nader:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.rent_free_months_description) }), "."] })), _jsx(FieldRow, { label: "Hyrestyp", children: v(values.rent_type) }), _jsx(FieldRow, { label: "Avgift till hyresg\u00E4storg. ing\u00E5r", children: v(values.rent_includes_member_fee) }), _jsx(FieldRow, { label: "Medlemskap i hyresg\u00E4storg. ing\u00E5r", children: v(values.rent_includes_membership) })] }), _jsxs(Section, { sectionRef: "tillagg", title: "Till\u00E4gg", children: [_jsx(FieldRow, { label: "Uppv\u00E4rmning", children: v(values.heating_included) }), _jsx(FieldRow, { label: "Kallvatten", children: v(values.cold_water_included) }), _jsx(FieldRow, { label: "Varmvatten", children: v(values.hot_water_included) }), _jsx(FieldRow, { label: "Hush\u00E5llsel", children: v(values.electricity_included) }), _jsx(FieldRow, { label: "Tillval av utrustning", children: values.extras_included === true ? '☑' : '☐' }), cond(mode, values.extras_included === true) && (_jsx(FieldRow, { label: "Beskrivning", children: v(values.extras_description) }))] }), _jsx(Section, { sectionRef: "korridorstadning", title: "Korridor- och trappst\u00E4dning", children: _jsx(FieldRow, { label: "St\u00E4dning", children: v(values.corridor_cleaning) }) }), _jsxs(Section, { sectionRef: "betalning", title: "Hyrans betalning", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Hyran ska betalas i f\u00F6rskott senast sista vardagen f\u00F6re varje kalenderm\u00E5nads b\u00F6rjan." }), _jsx(FieldRow, { label: "Betalningss\u00E4tt", children: v(values.payment_type) }), cond(mode, values.payment_type === 'Plusgiro' || values.payment_type === 'Bankgiro') && _jsx(FieldRow, { label: "Kontonummer", children: v(values.payment_account) }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: "Vid f\u00F6rsenad hyresbetalning ska hyresg\u00E4sten betala ers\u00E4ttning f\u00F6r skriftlig betalningsp\u00E5minnelse enligt lagstiftningen om ers\u00E4ttning f\u00F6r inkassokostnader." })] }), _jsxs(Section, { sectionRef: "deposition", title: "Deposition", children: [_jsx(FieldRow, { label: "Deposition kr\u00E4vs", children: v(values.deposit_required) }), cond(mode, values.deposit_required === 'JA') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Depositionsbelopp (m\u00E5nadshyror)", children: num(values.deposit_months) }), _jsx(FieldRow, { label: "\u00C5terbetalning inom (dagar)", children: num(values.deposit_return_days) }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: "Hyresg\u00E4sten ska till hyresv\u00E4rden erl\u00E4gga deposition senast i samband med tilltr\u00E4det. Depositionen utg\u00F6r s\u00E4kerhet f\u00F6r hyresg\u00E4stens samtliga f\u00F6rpliktelser enligt detta avtal, inklusive men inte begr\u00E4nsat till betalning av hyra, ers\u00E4ttning f\u00F6r skador p\u00E5 l\u00E4genheten samt andra kostnader h\u00E4nf\u00F6rliga till hyresf\u00F6rh\u00E5llandet." })] }))] }), _jsxs(Section, { sectionRef: "garage", title: "Garage / Bilplats", children: [_jsx(FieldRow, { label: "Uppl\u00E5telse", children: v(values.parking_arrangement) }), cond(mode, values.parking_arrangement ===
                        'Avgift för garage / bilplats ingår i hyran') && (_jsx(FieldRow, { label: "Plats nr", children: v(values.parking_spot_number) })), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: "Hyresg\u00E4sten f\u00E5r inte utan hyresv\u00E4rdens skriftliga medgivande \u00F6verl\u00E5ta hyresr\u00E4tten eller s\u00E4tta annan i sitt st\u00E4lle." })] }), _jsx(Section, { sectionRef: "reservnycklar", title: "Reservnycklar", children: _jsx(FieldRow, { label: "Hyresg\u00E4sten medger hyresv\u00E4rden r\u00E4tt att inneha reservnyckel", children: v(values.spare_key_consent) }) }), _jsx(Section, { sectionRef: "sarskilda-bestammelser", title: "S\u00E4rskilda best\u00E4mmelser", children: _jsxs("ol", { className: "ml-4 list-decimal space-y-2 text-[10.5px] leading-relaxed", children: [_jsx("li", { children: "L\u00E4genheten \u00E4r avsedd f\u00F6r person som bedriver aktiva universitets-, h\u00F6gskole- eller d\u00E4rmed j\u00E4mf\u00F6rliga studier. Pr\u00F6vning av hyresg\u00E4stens behov av studentbostad kan d\u00E4rf\u00F6r ske under hyrestiden. Hyresf\u00F6rh\u00E5llandet ska dock senast upph\u00F6ra n\u00E4r hyresg\u00E4sten \u00E4r klar med sina studier. Med h\u00E4nsyn till normalstudietid \u00E4r avsikten att hyresf\u00F6rh\u00E5llandet efter upps\u00E4gning ska upph\u00F6ra efter maximalt 5 \u00E5r. P\u00E5 hyresg\u00E4stens skriftliga beg\u00E4ran kan hyresv\u00E4rden bevilja f\u00F6rl\u00E4ngning under f\u00F6ruts\u00E4ttning att aktiva studier p\u00E5g\u00E5r eller att det finns andra synnerliga sk\u00E4l till f\u00F6rl\u00E4ngning." }), cond(mode, values.inventory_included === true) && (_jsxs("li", { children: ["L\u00E4genheten hyrs ut med inventarier och annan utrustning enligt s\u00E4rskild f\u00F6rteckning:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.inventory_description) }), "."] })), cond(mode, values.is_corridor_room === true) && (_jsx("li", { children: "Hyresg\u00E4sten ansvarar f\u00F6r st\u00E4dning av l\u00E4genheten och gemensamma utrymmen som ing\u00E5r i uppl\u00E5telsen." })), cond(mode, values.is_corridor_room === false) && (_jsx("li", { children: "Hyresg\u00E4sten ansvarar f\u00F6r st\u00E4dning av l\u00E4genheten." }))] }) }), _jsx(ClauseBlock, { title: "R\u00F6kf\u00F6rbud", body: "R\u00F6kning \u00E4r inte till\u00E5ten i l\u00E4genheten eller husets allm\u00E4nna och gemensamma utrymmen." }), _jsx(ClauseBlock, { title: "Hyresg\u00E4stens \u00E5taganden", body: 'Hyresgästen förbinder sig:\n' +
                    '• att iaktta sin vårdplikt i sin användning av lägenheten och vad som hör till den samt vid användning av de gemensamma utrymmena.\n' +
                    '• att inte utan hyresvärdens samtycke eller hyresnämndens tillstånd upplåta lägenheten i andra hand.\n' +
                    '• att inte låta fler personer stadigvarande bo i lägenheten än vad den är avsedd för.\n' +
                    '• att på egen bekostnad forsla bort avfall som är olämpligt för soptunna eller sopnedkast.\n' +
                    '• att hålla till lägenheten hörande balkong eller uteplats fri från snö och is.\n' +
                    '• att lämna hyresvärden tillträde till lägenheten för tillsyn eller förbättringsarbete.\n' +
                    '• att när lägenheten sagts upp, låta den visas för ny hyresgäst på lämplig tid.\n' +
                    '• att vid flytt överlämna samtliga port- och dörrnycklar samt städa lägenheten noggrant.' }), _jsx(ClauseBlock, { title: "Force majeure", body: "Hyresv\u00E4rden fritar sig fr\u00E5n skyldighet att fullg\u00F6ra sin del av avtalet och fr\u00E5n skyldighet att betala skadest\u00E5nd om \u00E5tagandena inte alls eller endast till onormalt h\u00F6g kostnad kan fullg\u00F6ras p\u00E5 grund av krig, upplopp, arbetsinst\u00E4llelse, blockad, eldsv\u00E5da, explosion eller ingrepp av offentlig myndighet som hyresv\u00E4rden inte r\u00E5der \u00F6ver och inte heller kunnat f\u00F6rutse." }), _jsx(ClauseBlock, { title: "Tidigare hyresavtal", body: "Eventuellt tidigare hyresavtal mellan parterna avseende denna l\u00E4genhet upph\u00F6r att g\u00E4lla fr.o.m. dagen f\u00F6r detta avtals ikrafttr\u00E4dande." }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresvärden', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgästen', role: 'lessee', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map